
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';
import type { Tables } from '@/integrations/supabase/types';

type BoardMember = Tables<'board_members'> & {
  profiles?: {
    id: string;
    full_name: string | null;
    username: string | null;
    avatar_url: string | null;
  } | null;
};
type BoardInvitation = Tables<'board_invitations'>;

export const useBoardCollaboration = (boardId: string | null) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch board members
  const { data: boardMembers, isLoading: membersLoading } = useQuery({
    queryKey: ['boardMembers', boardId],
    queryFn: async (): Promise<BoardMember[]> => {
      if (!boardId) return [];
      
      // First get board members
      const { data: membersData, error: membersError } = await supabase
        .from('board_members')
        .select('*')
        .eq('board_id', boardId)
        .order('role', { ascending: true });

      if (membersError) {
        console.error('Error fetching board members:', membersError);
        throw membersError;
      }

      if (!membersData || membersData.length === 0) {
        return [];
      }

      // Get user IDs to fetch profiles
      const userIds = membersData.map(member => member.user_id);

      // Fetch profiles separately
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, username, avatar_url')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        // Don't throw error, just continue without profiles
      }

      // Combine members with their profiles
      const membersWithProfiles: BoardMember[] = membersData.map(member => ({
        ...member,
        profiles: profilesData?.find(profile => profile.id === member.user_id) || null
      }));

      return membersWithProfiles;
    },
    enabled: !!boardId
  });

  // Fetch board invitations
  const { data: boardInvitations, isLoading: invitationsLoading } = useQuery({
    queryKey: ['boardInvitations', boardId],
    queryFn: async () => {
      if (!boardId) return [];
      
      const { data, error } = await supabase
        .from('board_invitations')
        .select('*')
        .eq('board_id', boardId)
        .is('accepted_at', null)
        .order('invited_at', { ascending: false });

      if (error) throw error;
      return data as BoardInvitation[];
    },
    enabled: !!boardId
  });

  // Get current user's role in the board
  const currentUserRole = boardMembers?.find(member => member.user_id === user?.id)?.role;
  const canManageMembers = currentUserRole && ['owner', 'admin'].includes(currentUserRole);

  // Invite user mutation
  const inviteUserMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      if (!boardId || !user) throw new Error('Missing required data');
      
      // First create the invitation
      const { data: invitation, error } = await supabase
        .from('board_invitations')
        .insert({
          board_id: boardId,
          email,
          role: role as any,
          invited_by: user.id
        })
        .select()
        .single();
      
      if (error) throw error;

      // Get board details and inviter profile for email
      const { data: board } = await supabase
        .from('boards')
        .select('title')
        .eq('id', boardId)
        .single();

      const { data: inviterProfile } = await supabase
        .from('profiles')
        .select('full_name, username')
        .eq('id', user.id)
        .single();

      const inviterName = inviterProfile?.full_name || inviterProfile?.username || 'Someone';
      const boardTitle = board?.title || 'Untitled Board';

      // Send the invitation email
      const { error: emailError } = await supabase.functions.invoke('send-board-invitation', {
        body: {
          invitationId: invitation.id,
          boardTitle,
          inviterName
        }
      });

      if (emailError) {
        console.error('Error sending invitation email:', emailError);
        // Don't throw error, invitation is created but email failed
        toast({
          title: "Invitation Created",
          description: "Invitation created but email sending failed. Please share the invitation manually.",
          variant: "destructive"
        });
      }

      return invitation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boardInvitations', boardId] });
      toast({
        title: "Success",
        description: "Invitation sent successfully!"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Update member role mutation
  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: string }) => {
      const { error } = await supabase
        .from('board_members')
        .update({ role: role as any })
        .eq('id', memberId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boardMembers', boardId] });
      toast({
        title: "Success",
        description: "Member role updated successfully!"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('board_members')
        .delete()
        .eq('id', memberId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boardMembers', boardId] });
      toast({
        title: "Success",
        description: "Member removed successfully!"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Cancel invitation mutation
  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from('board_invitations')
        .delete()
        .eq('id', invitationId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boardInvitations', boardId] });
      toast({
        title: "Success",
        description: "Invitation cancelled successfully!"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  return {
    boardMembers,
    boardInvitations,
    currentUserRole,
    canManageMembers,
    membersLoading,
    invitationsLoading,
    inviteUser: inviteUserMutation.mutate,
    updateMemberRole: updateMemberRoleMutation.mutate,
    removeMember: removeMemberMutation.mutate,
    cancelInvitation: cancelInvitationMutation.mutate,
    isInviting: inviteUserMutation.isPending,
    isUpdatingRole: updateMemberRoleMutation.isPending,
    isRemovingMember: removeMemberMutation.isPending,
    isCancellingInvitation: cancelInvitationMutation.isPending
  };
};
